import { useState } from 'react';
import { Checkbox } from 'antd5';
import { StyledTable } from './InspectionTaskChooser.style';
import * as taskData from 'components/data/tasks/tasks';

import { useAccountContext } from 'utils/account/AccountContext';
import { WORK_CONTAINER_FILTER_IMPROVEMENT } from 'utils/release-toggles';
import { useFeatures } from 'utils/features';
import { useSelectableRecords } from 'utils/selectable';
import { STANDARD_COLUMN_SCROLL_Y } from 'utils/constants';

import {
  AtlasGqlFilterOperator,
  AtlasGqlInspectionTask,
  AtlasGqlWorkContainerTypeOption,
  useGetInspectionTasksQuery,
} from 'types/atlas-graphql';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';

interface InspectionTaskChooserProps {
  assetId?: string;
  onChange: (value?: string | null) => void;
}
export const InspectionTaskChooser = ({ assetId, onChange }: InspectionTaskChooserProps) => {
  const { hasReleaseToggle } = useAccountContext();
  const hasFilterImprovementRT = hasReleaseToggle(WORK_CONTAINER_FILTER_IMPROVEMENT);
  const { WORK_ORDERS } = useFeatures().features;

  const [selectedTaskId, setSelectedTaskId] = useState<string | null | undefined>();

  // get eligible tasks (inspection tasks that are non archived)
  const { data: inspectionTasksData, loading: inspectionTasksLoading } = useGetInspectionTasksQuery(
    {
      variables: {
        assetIds: [assetId as string],
        offset: 0,
        limit: 0,
        filterBy: [
          {
            key: 'type',
            values: [AtlasGqlWorkContainerTypeOption.InspectionTask],
            operator: AtlasGqlFilterOperator.Equals,
          },
          {
            key: 'archived',
            values: [],
            operator: AtlasGqlFilterOperator.IsNull,
          },
        ],
        sortBy: [],
      },
      fetchPolicy: 'network-only',
      skip: !assetId,
    }
  );

  // filter out inspections tasks that have inspections
  const filteredTasks = (inspectionTasksData?.paginatedTasks?.items ?? []).filter(task => {
    return !(task as AtlasGqlInspectionTask).inspection;
  });

  // handle selected to create a new task
  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    onClearSelection();

    const value = e.target.checked ? null : undefined;
    setSelectedTaskId(value);
    onChange(value);
  };

  // handle selections in the table;
  const handleSelectionChange = (selected: AtlasGqlInspectionTask[]) => {
    const value = selected[0]?.id;
    setSelectedTaskId(value);
    onChange(value);
  };

  const { rowSelection, onClearSelection } =
    useSelectableRecords<AtlasGqlInspectionTask>(handleSelectionChange);

  return (
    <>
      <Checkbox checked={selectedTaskId === null} onChange={handleCheckboxChange}>
        Create new Task
      </Checkbox>
      {assetId && selectedTaskId !== null && (
        // @ts-ignore JZ: it doesn't like some of the column definitions.
        <StyledTable
          data-testid="tasks-selection-table"
          loading={inspectionTasksLoading}
          columns={[
            taskData.taskNumber,
            ...(WORK_ORDERS ? [taskData.atlasWorkOrder(15)] : []),
            taskData.clientAtlasType,
            taskData.status,
            taskData.dueDate(hasFilterImprovementRT),
            taskData.created,
          ]}
          dataSource={filteredTasks}
          rowSelection={{
            ...rowSelection,
            type: 'radio',
            hideSelectAll: true,
          }}
          rowKey="id"
          size="small"
          pagination={false}
          scroll={STANDARD_COLUMN_SCROLL_Y}
        />
      )}
    </>
  );
};
